import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { isFileAllowed } from 'src/app/shared/utils/file-validation.utils';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChunkService } from '../chunk/chunk.service';
import { ContentBuilderDraft, MermaidGeneratorService } from '../mermaid-generator.service';
import { ContentSearcherComponent, ContentSearchResult } from '../content-searcher/content-searcher.component';
import { ContentStateService } from '../content-state.service';
import { MermaidDialogComponent } from '../chunk/chunk-displayer/displayers/mermaid-dialog.component';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';

@Component({
  selector: 'app-mermaid-generator',
  templateUrl: './mermaid-generator.component.html',
  styleUrls: ['./mermaid-generator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    TranslateModule,
    SelectComponent,
  ],
})
export class MermaidGeneratorComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private mermaidGeneratorService = inject(MermaidGeneratorService);
  private contentStateService = inject(ContentStateService);
  private chunkService = inject(ChunkService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  private confirmMenuService = inject(ConfirmMenuService);
  private dynamicOverlayService = inject(DynamicOverlayService);
  private errorHandler = inject(ErrorHandlerService);
  private dialog = inject(MatDialog);

  @Input() courseID: string;
  @Output() close = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  viewMode = signal<'LIST' | 'SETUP' | 'LOADING'>('LIST');

  aiSetupForm: FormGroup;
  drafts = signal<ContentBuilderDraft[]>([]);
  visibleDrafts = computed(() => this.drafts().filter((d) => d.status !== 'draft'));

  attachedFiles = signal<{ id: string; file?: File; url?: string; filename?: string; type: 'audio' | 'file' | 'video' | 'image'; name: string; uploading?: boolean }[]>([]);
  attachedExternalContexts = signal<ContentSearchResult[]>([]);
  insertingDraftIds = signal<Set<string>>(new Set());

  cannotUseAI = computed(() => !this.authService.canUseAI());

  activeDraftId = signal<string | null>(null);
  interactionId = signal<string | null>(null);

  dragCounter = 0;
  isDraggingOver = signal(false);

  diagramTypeOptions: SelectOption[] = [
    { value: 'auto', label: 'GenerateContent.Mermaid.TypeAuto' },
    { value: 'flowchart', label: 'GenerateContent.Mermaid.TypeFlowchart' },
    { value: 'sequenceDiagram', label: 'GenerateContent.Mermaid.TypeSequence' },
    { value: 'classDiagram', label: 'GenerateContent.Mermaid.TypeClass' },
    { value: 'stateDiagram', label: 'GenerateContent.Mermaid.TypeState' },
    { value: 'erDiagram', label: 'GenerateContent.Mermaid.TypeER' },
    { value: 'gantt', label: 'GenerateContent.Mermaid.TypeGantt' },
    { value: 'pie', label: 'GenerateContent.Mermaid.TypePie' },
    { value: 'gitGraph', label: 'GenerateContent.Mermaid.TypeGit' },
    { value: 'mindmap', label: 'GenerateContent.Mermaid.TypeMindmap' },
    { value: 'timeline', label: 'GenerateContent.Mermaid.TypeTimeline' },
    { value: 'graph', label: 'GenerateContent.Mermaid.TypeGraph' },
  ];

  modelOptions: SelectOption[] = [
    { value: 'fast', label: 'GenerateContent.Mermaid.ModelFast' },
    { value: 'pro', label: 'GenerateContent.Mermaid.ModelPro' },
  ];

  constructor() {
    this.aiSetupForm = this.fb.group({
      subject: ['', Validators.required],
      diagramType: ['auto'],
      customization: [''],
      model: ['fast', Validators.required],
    });
  }

  ngOnInit(): void {
    if (!this.courseID) {
      this.courseID = this.contentStateService.courseId;
    }

    const lectureSubject = this.contentStateService.lecture?.subject;
    if (lectureSubject) {
      this.aiSetupForm.patchValue({ subject: lectureSubject });
    }

    this.aiSetupForm.valueChanges.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe((val) => {
      if (this.activeDraftId()) {
        this.mermaidGeneratorService
          .updateDraft(this.activeDraftId()!, {
            subject: val.subject,
            customization: val.customization,
            model: val.model,
          })
          .subscribe({
            next: (updatedDraft) => this.updateLocalDraft(updatedDraft),
            error: (err) => console.error('Failed to auto-save draft', err),
          });
      }
    });

    this.loadDrafts();
  }

  loadDrafts(): void {
    this.mermaidGeneratorService.getDrafts(this.courseID).subscribe({
      next: (drafts) => this.drafts.set(drafts),
      error: (err) => console.error('Failed to load drafts', err),
    });
  }

  openAiSetup(): void {
    this.viewMode.set('SETUP');
    this.activeDraftId.set(null);
    this.interactionId.set(null);
    this.attachedFiles.set([]);
    this.attachedExternalContexts.set([]);
    this.aiSetupForm.reset({
      subject: '',
      diagramType: 'auto',
      customization: '',
      model: 'fast',
    });
  }

  goBackToList(): void {
    this.viewMode.set('LIST');
    this.loadDrafts();
  }

  cancelSetup(): void {
    this.viewMode.set('LIST');
    this.activeDraftId.set(null);
    this.interactionId.set(null);
    this.attachedFiles.set([]);
    this.attachedExternalContexts.set([]);
    this.loadDrafts();
  }

  goBack(): void {
    this.goBackToList();
  }

  openDraft(draft: ContentBuilderDraft): void {
    const mermaidContent = draft.versions?.find((v) => v._id === draft.currentVersionId)?.content || draft.content || '';
    if (!mermaidContent.trim()) return;

    const dialogId = draft._id || Date.now().toString();
    const mermaidDialogRef = this.dialog.open(MermaidDialogComponent, {
      data: {
        content: mermaidContent,
        title: draft.title || 'Mermaid Diagram',
        id: dialogId,
      },
      panelClass: 'mermaid-dialog-panel',
      width: '95vw',
      height: '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    mermaidDialogRef.afterClosed().subscribe((insertRequested: boolean) => {
      if (insertRequested) {
        this.insertMermaidChunk(mermaidContent, draft.title || 'Mermaid Diagram');
      }
    });
  }

  generateMermaidDiagram(): void {
    if (this.aiSetupForm.invalid) {
      this.aiSetupForm.markAllAsTouched();
      return;
    }

    const { subject, diagramType, customization, model } = this.aiSetupForm.getRawValue();
    this.viewMode.set('LOADING');

    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    this.mermaidGeneratorService
      .generateMermaid({
        draftId: this.activeDraftId() || undefined,
        contentId: this.courseID,
        subject,
        customization,
        model,
        diagramType: diagramType === 'auto' ? undefined : diagramType,
        interactionId: this.interactionId() || undefined,
        mediaContexts: readyAttachments.length > 0 ? readyAttachments.map((a) => ({ url: a.url!, type: a.type, filename: a.name })) : undefined,
        attachedContentIds: this.attachedExternalContexts().filter((c) => c.type !== 'chunk').map((c) => c.id),
        attachedChunkIds: this.attachedExternalContexts().filter((c) => c.type === 'chunk').map((c) => c.id),
      })
      .subscribe({
        next: (draft) => {
          this.loadDrafts();
          this.viewMode.set('LIST');
          this.interactionId.set(null);
          this.activeDraftId.set(null);
          this.attachedFiles.set([]);
          this.attachedExternalContexts.set([]);
          // Open preview automatically
          const mermaidContent = draft.versions?.find((v) => v._id === draft.currentVersionId)?.content || draft.content || '';
          if (mermaidContent.trim()) {
            this.openMermaidPreview(draft);
          }
        },
        error: (err) => {
          console.error('Mermaid generation failed', err);
          this.errorHandler.handleAiRestrictedError(err);
          this.viewMode.set('SETUP');
        },
      });
  }

  openMermaidPreview(draft: ContentBuilderDraft): void {
    const mermaidContent = draft.versions?.find((v) => v._id === draft.currentVersionId)?.content || draft.content || '';
    if (!mermaidContent.trim()) return;

    const dialogId = draft._id || Date.now().toString();
    const mermaidDialogRef = this.dialog.open(MermaidDialogComponent, {
      data: {
        content: mermaidContent,
        title: draft.title || 'Mermaid Diagram',
        id: dialogId,
      },
      panelClass: 'mermaid-dialog-panel',
      width: '95vw',
      height: '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    mermaidDialogRef.afterClosed().subscribe((insertRequested: boolean) => {
      if (insertRequested) {
        this.insertMermaidChunk(mermaidContent, draft.title || 'Mermaid Diagram');
      }
    });
  }

  insertMermaidChunk(mermaidContent: string, title: string): void {
    const chunkParams = {
      courseID: this.contentStateService.courseId,
      contentID: this.courseID,
      lecture: this.contentStateService.lecture,
      section: this.contentStateService.section,
      noteID: this.contentStateService.noteId,
      blogID: this.contentStateService.blogId,
      contentName: this.contentStateService.contentName,
      contentType: this.contentStateService.contentType,
      type: 'code',
      codeLanguage: 'mermaid',
      language: this.translate.currentLang,
      content: mermaidContent,
      title: title,
      tags: [],
      generateTranslation: false,
      generateVoice: false,
      generateEmbeddings: false,
    };

    this.chunkService
      .createChunk({
        chunks: [chunkParams],
        insertBelowID: undefined,
      })
      .subscribe({
        next: () => {
          this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Close', { duration: 2000 });
          this.close.emit();
        },
        error: (error) => {
          console.error('Failed to create mermaid chunk', error);
          this.errorHandler.handleAiRestrictedError(error);
        },
      });
  }

  confirmInsertDraft(event: Event, draft: ContentBuilderDraft, trigger: MatIconButton): void {
    event.stopPropagation();
    const mermaidContent = draft.versions?.find((v) => v._id === draft.currentVersionId)?.content || draft.content || '';
    if (!mermaidContent.trim()) return;
    this.insertMermaidChunk(mermaidContent, draft.title || 'Mermaid Diagram');
  }

  deleteDraft(draftId: string, event: Event, trigger: MatIconButton): void {
    event.stopPropagation();
    this.confirmMenuService.confirm(trigger._elementRef, 'Global.AreYouSure', 'delete_outline').subscribe(() => {
      this.mermaidGeneratorService.deleteDraft(draftId).subscribe(() => {
        this.loadDrafts();
      });
    });
  }

  updateLocalDraft(updatedDraft: ContentBuilderDraft): void {
    this.drafts.update((drafts) => drafts.map((d) => (d._id === updatedDraft._id ? updatedDraft : d)));
  }

  allFilesDoneUploading(): boolean {
    return this.attachedFiles().every((f) => !f.uploading);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.cannotUseAI()) {
      this.dragCounter++;
      if (this.dragCounter === 1) this.isDraggingOver.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDraggingOver.set(false);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDraggingOver.set(false);
    if (this.cannotUseAI()) return;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) this.handleFiles(files);
  }

  async handleFiles(files: FileList): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!isFileAllowed(file)) {
        this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
        continue;
      }
      let mediaType: 'audio' | 'file' | 'video' | 'image' = 'file';
      if (file.type.startsWith('audio/')) mediaType = 'audio';
      else if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('image/')) mediaType = 'image';
      const fileId = Date.now().toString() + '_' + i;
      this.attachedFiles.update((arr) => [...arr, { id: fileId, file, name: file.name, type: mediaType, uploading: true }]);
      this.uploadFileInternal(file, fileId);
    }
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;
    this.handleFiles(files);
    event.target.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string): Promise<void> {
    try {
      const filename = `content_generator_attachments/${this.contentStateService.courseId || this.contentStateService.noteId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;
      this.attachedFiles.update((arr) => arr.map((f) => (f.id === fileId ? { ...f, url: gsUrl, uploading: false } : f)));
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string): void {
    this.attachedFiles.update((arr) => arr.filter((f) => f.id !== fileId));
  }

  removeExternalContext(id: string): void {
    this.attachedExternalContexts.update((contexts) => contexts.filter((c) => c.id !== id));
  }

  private cdkOverlay = inject(Overlay);
  private searcherOverlayRef: OverlayRef | null = null;

  toggleContentSearcher(): void {
    if (this.searcherOverlayRef) {
      this.searcherOverlayRef.dispose();
      this.searcherOverlayRef = null;
      return;
    }
    const positionStrategy = this.cdkOverlay.position().global().centerHorizontally().centerVertically();
    this.searcherOverlayRef = this.cdkOverlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });
    const portal = new ComponentPortal(ContentSearcherComponent);
    const compRef = this.searcherOverlayRef.attach(portal);
    compRef.instance.itemSelected.subscribe((result: ContentSearchResult) => this.onContentSearcherSelect(result));
    compRef.instance.closed.subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
    this.searcherOverlayRef.backdropClick().subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
  }

  async onContentSearcherSelect(result: ContentSearchResult): Promise<void> {
    if (!result?.id) return;
    if (this.attachedExternalContexts().find((c) => c.id === result.id)) return;
    this.attachedExternalContexts.update((contexts) => [...contexts, result]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
